'use client';
import {
  Sparkles,
  LayoutDashboard,
  Store,
  MessageCircle,
  TrendingUp,
  FileCheck,
  Clock,
} from 'lucide-react';
import { useTranslations } from 'next-intl';

export function Solution() {
  const t = useTranslations('landing.solution');

  const pillars = [
    {
      icon: Store,
      title: t('pillar1Title'),
      description: t('pillar1Desc'),
      color: 'from-amber-500/20 to-amber-500/5',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
    },
    {
      icon: LayoutDashboard,
      title: t('pillar2Title'),
      description: t('pillar2Desc'),
      color: 'from-violet-500/20 to-violet-500/5',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
    },
    {
      icon: Sparkles,
      title: t('pillar3Title'),
      description: t('pillar3Desc'),
      color: 'from-primary/20 to-primary/5',
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary',
    },
  ] as const;

  const features = [
    {
      icon: Sparkles,
      title: t('feature1Title'),
      description: t('feature1Desc'),
      stat: '3 sec',
      statLabel: t('feature1StatLabel'),
    },
    {
      icon: TrendingUp,
      title: t('feature2Title'),
      description: t('feature2Desc'),
      stat: '100%',
      statLabel: t('feature2StatLabel'),
    },
    {
      icon: FileCheck,
      title: t('feature3Title'),
      description: t('feature3Desc'),
      stat: '0',
      statLabel: t('feature3StatLabel'),
    },
    {
      icon: MessageCircle,
      title: t('feature4Title'),
      description: t('feature4Desc'),
      stat: '24/7',
      statLabel: t('feature4StatLabel'),
    },
    {
      icon: Clock,
      title: t('feature5Title'),
      description: t('feature5Desc'),
      stat: '10x',
      statLabel: t('feature5StatLabel'),
    },
  ] as const;

  return (
    <section id="features" className="section-padding relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 gradient-mesh" />

      <div className="container-landing relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent text-sm font-medium mb-6 border border-primary/20">
            {t('sectionBadge')}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display tracking-tight text-balance">
            {t('heading')}{' '}
            <span className="text-gradient">{t('headingHighlight')}</span>
          </h2>
          <p className="mt-4 md:mt-6 text-lg md:text-xl text-muted-foreground text-balance">
            {t('subheading')}
          </p>
        </div>

        {/* Three pillars */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-20 md:mb-28">
          {pillars.map((pillar, index) => (
            <div key={pillar.title} className="group relative">
              <div
                className={`h-full p-8 rounded-2xl bg-gradient-to-b ${pillar.color} border border-white/50 backdrop-blur-sm transition-all duration-300 hover:shadow-xl`}
              >
                {/* Number badge */}
                <div className="absolute -top-3 left-6 px-3 py-1 bg-foreground text-background text-xs font-bold rounded-full">
                  0{index + 1}
                </div>

                {/* Icon */}
                <div
                  className={`w-14 h-14 ${pillar.iconBg} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}
                >
                  <pillar.icon className={`w-7 h-7 ${pillar.iconColor}`} />
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold mb-3">{pillar.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {pillar.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Connecting visual element */}
        <div className="flex justify-center mb-16 md:mb-20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-border" />
            <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
            <span className="text-sm font-medium text-muted-foreground px-4">
              {t('featuresConnector')}
            </span>
            <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-border" />
          </div>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="group p-6 rounded-2xl bg-card border hover:border-primary/30 hover:shadow-lg transition-all duration-300"
            >
              {/* Icon */}
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <feature.icon className="w-6 h-6 text-primary" />
              </div>

              {/* Content */}
              <h4 className="font-semibold mb-2">{feature.title}</h4>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                {feature.description}
              </p>

              {/* Stat */}
              <div className="pt-4 border-t">
                <div className="text-2xl font-display text-primary">
                  {feature.stat}
                </div>
                <div className="text-xs text-muted-foreground">
                  {feature.statLabel}
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
